/**
 * Company bonus calculator for Finances planned obligations.
 * Pure — no Firebase. Employer tax uses supplemental-wage remaining wage bases.
 */
import type {
  BonusPlan,
  BonusPayoutSlice,
  ObligationSettings,
  ObligationSlice,
  PayrollPosition,
  PayrollSettings,
} from '../types/finances';
import {
  createDefaultBonusPlan,
  DEFAULT_OBLIGATION_SETTINGS,
  DEFAULT_PAYROLL_SETTINGS,
} from '../types/finances';
import {
  computeEmployerTaxOnSupplementalWages,
  resolveWorkState,
} from './payrollEmployerTax';
import { resolvePositionForMonth } from './payrollCompSchedule';

export interface BonusPositionContext {
  crmUserLocation?: string | null;
}

export interface BonusPersonResult {
  positionId: string;
  title: string;
  department: PayrollPosition['department'];
  included: boolean;
  annualSalary: number;
  /** % of year-end profit used for this person when mode is percent_of_profit. */
  profitPercent: number | null;
  grossBonus: number;
  employerTax: number;
  cashOut: number;
}

export interface ComputedBonusPlan {
  year: number;
  enabled: boolean;
  people: BonusPersonResult[];
  eligibleCount: number;
  estimatedProfit: number;
  bonusPool: number;
  profitAfterBonuses: number;
  grossTotal: number;
  employerTaxTotal: number;
  cashOutTotal: number;
  slices: ObligationSlice[];
}

function roundMoney(n: number): number {
  return Math.round(n);
}

function clampDay(year: number, month1to12: number, day: number): Date {
  const monthIdx = Math.min(12, Math.max(1, month1to12)) - 1;
  const last = new Date(year, monthIdx + 1, 0).getDate();
  const d = Math.min(last, Math.max(1, Math.round(day) || 1));
  return new Date(year, monthIdx, d);
}

export function isBonusEligiblePosition(position: PayrollPosition): boolean {
  if (!position.isActive) return false;
  if (position.status !== 'filled' && position.status !== 'open') return false;
  return true;
}

function overrideFor(plan: BonusPlan, positionId: string) {
  return plan.overrides.find((o) => o.positionId === positionId);
}

function personIncluded(plan: BonusPlan, position: PayrollPosition): boolean {
  const ov = overrideFor(plan, position.id);
  if (ov) return ov.included;
  return isBonusEligiblePosition(position);
}

function lumpDate(plan: BonusPlan): Date {
  return clampDay(plan.year, plan.payoutMonth, plan.payoutDay);
}

function payoutPercents(plan: BonusPlan): Array<{ date: Date; percent: number; label?: string }> {
  if (plan.payoutSplit === 'custom' && plan.customSlices.length > 0) {
    const totalPct = plan.customSlices.reduce((s, c) => s + (Number(c.percent) || 0), 0);
    if (totalPct <= 0) return [{ date: lumpDate(plan), percent: 100, label: 'Bonus' }];
    return plan.customSlices.map((c) => ({
      date: c.date instanceof Date ? c.date : lumpDate(plan),
      percent: ((Number(c.percent) || 0) / totalPct) * 100,
      label: c.label,
    }));
  }
  return [{ date: lumpDate(plan), percent: 100, label: 'Year-end bonus' }];
}

function allocatePool(eligibleSalaries: number[], pool: number): number[] {
  const total = eligibleSalaries.reduce((s, n) => s + n, 0);
  if (pool <= 0) return eligibleSalaries.map(() => 0);
  if (total <= 0) {
    const each = roundMoney(pool / eligibleSalaries.length);
    const out = eligibleSalaries.map(() => each);
    const drift = pool - out.reduce((s, n) => s + n, 0);
    if (out.length) out[out.length - 1] += drift;
    return out;
  }
  const out = eligibleSalaries.map((sal) => roundMoney((pool * sal) / total));
  const drift = pool - out.reduce((s, n) => s + n, 0);
  if (out.length) out[out.length - 1] += drift;
  return out;
}

export function resolveBonusPool(plan: BonusPlan, estimatedProfit = 0): number {
  if (plan.mode === 'profit_pool') {
    return roundMoney(Number(plan.poolAmount) || 0);
  }
  return 0;
}

export function personProfitPercent(plan: BonusPlan, positionId: string): number {
  const ov = overrideFor(plan, positionId);
  if (ov?.percent != null && Number.isFinite(ov.percent)) return Math.max(0, ov.percent);
  return Math.max(0, Number(plan.defaultPercent) || 0);
}

export function computeBonusGross(
  plan: BonusPlan,
  position: PayrollPosition,
  poolShare: number | null,
  estimatedProfit = 0,
): number {
  const ov = overrideFor(plan, position.id);
  if (ov?.amount != null && Number.isFinite(ov.amount) && ov.amount > 0) {
    return roundMoney(ov.amount);
  }
  if (plan.mode === 'percent_of_profit') {
    return roundMoney(Math.max(0, estimatedProfit) * (personProfitPercent(plan, position.id) / 100));
  }
  if (plan.mode === 'profit_pool') {
    return roundMoney(poolShare ?? 0);
  }
  if (plan.mode === 'fixed') {
    return roundMoney(Number(plan.defaultFixedAmount) || 0);
  }
  const pct = ov?.percent != null && Number.isFinite(ov.percent)
    ? ov.percent
    : plan.defaultPercent;
  return roundMoney((position.annualSalary || 0) * (Math.max(0, pct) / 100));
}

export function computeBonusPlan(
  plan: BonusPlan,
  positions: PayrollPosition[],
  options: {
    payrollSettings?: PayrollSettings;
    locationByUserId?: Map<string, string | null>;
    now?: Date;
    estimatedProfit?: number;
  } = {},
): ComputedBonusPlan {
  const payrollSettings = options.payrollSettings ?? DEFAULT_PAYROLL_SETTINGS;
  const now = options.now ?? new Date();
  const estimatedProfit = Math.max(0, Number(options.estimatedProfit) || 0);
  const payouts = payoutPercents(plan);
  const firstPayout = payouts[0]?.date ?? lumpDate(plan);

  const resolved = positions.map((p) => resolvePositionForMonth(p, firstPayout));
  const included = resolved.filter((p) => personIncluded(plan, p));

  let poolShares: number[] | null = null;
  if (plan.mode === 'profit_pool') {
    poolShares = allocatePool(
      included.map((p) => {
        const ov = overrideFor(plan, p.id);
        if (ov?.amount != null && Number.isFinite(ov.amount) && ov.amount > 0) return 0;
        return p.annualSalary || 0;
      }),
      resolveBonusPool(plan),
    );
    included.forEach((p, i) => {
      const ov = overrideFor(plan, p.id);
      if (ov?.amount != null && Number.isFinite(ov.amount) && ov.amount > 0 && poolShares) {
        poolShares[i] = roundMoney(ov.amount);
      }
    });
  }

  const people: BonusPersonResult[] = resolved.map((position) => {
    const isIn = personIncluded(plan, position);
    const idx = included.findIndex((p) => p.id === position.id);
    const gross = isIn
      ? computeBonusGross(plan, position, poolShares && idx >= 0 ? poolShares[idx] : 0, estimatedProfit)
      : 0;
    const location = position.crmUserId
      ? options.locationByUserId?.get(position.crmUserId) ?? null
      : null;
    const workState = resolveWorkState(
      position.workState,
      location,
      payrollSettings.defaultWorkState,
    );
    const tax = isIn && plan.includeEmployerTax && gross > 0
      ? computeEmployerTaxOnSupplementalWages({
        supplementalWages: gross,
        annualWagesAlreadyPaid: position.annualSalary || 0,
        workState,
        flatOverrideRate: position.employerTaxRateOverride ?? null,
      }).total
      : 0;
    return {
      positionId: position.id,
      title: position.title,
      department: position.department,
      included: isIn,
      annualSalary: position.annualSalary || 0,
      profitPercent: plan.mode === 'percent_of_profit' ? personProfitPercent(plan, position.id) : null,
      grossBonus: roundMoney(gross),
      employerTax: roundMoney(tax),
      cashOut: roundMoney(gross + tax),
    };
  });

  const eligible = people.filter((p) => p.included);
  const grossTotal = eligible.reduce((s, p) => s + p.grossBonus, 0);
  const employerTaxTotal = eligible.reduce((s, p) => s + p.employerTax, 0);
  const cashOutTotal = eligible.reduce((s, p) => s + p.cashOut, 0);

  const slices: ObligationSlice[] = [];
  if (plan.enabled && cashOutTotal > 0) {
    const windowStart = new Date(now.getFullYear(), now.getMonth(), 1);
    let allocated = 0;
    const dated = payouts.map((payout, idx) => {
      const isLast = idx === payouts.length - 1;
      const amount = isLast
        ? cashOutTotal - allocated
        : roundMoney((cashOutTotal * payout.percent) / 100);
      allocated += amount;
      return { ...payout, amount };
    });
    dated.forEach((payout, idx) => {
      if (payout.amount <= 0) return;
      if (payout.date < windowStart) return;
      slices.push({
        plannedKind: 'bonus',
        projectLabel: 'Bonus',
        label: payout.label || (payouts.length > 1 ? `Bonus payment ${idx + 1}` : 'Year-end bonus'),
        amount: payout.amount,
        dueDate: payout.date,
        estimated: true,
      });
    });
  }

  return {
    year: plan.year,
    enabled: plan.enabled,
    people,
    eligibleCount: eligible.length,
    estimatedProfit,
    bonusPool: plan.mode === 'profit_pool' ? resolveBonusPool(plan) : grossTotal,
    profitAfterBonuses: roundMoney(estimatedProfit - cashOutTotal),
    grossTotal,
    employerTaxTotal,
    cashOutTotal,
    slices,
  };
}

export function newBonusSliceId(): string {
  return `bs-${Math.random().toString(36).slice(2, 10)}`;
}

export function createBlankBonusSlice(year: number, month = 12, day = 15): BonusPayoutSlice {
  return {
    id: newBonusSliceId(),
    date: clampDay(year, month, day),
    percent: 100,
    label: 'Bonus',
  };
}

export function bonusPlanFromSettings(
  year: number,
  settings: ObligationSettings = DEFAULT_OBLIGATION_SETTINGS,
): BonusPlan {
  return createDefaultBonusPlan(year, settings);
}
